/**
 * Tab bar and volunteers roster for volunteer opportunity detail modal.
 */
import React, { useMemo, useState } from "react";
import { formatPhoneDisplay } from "../shared/utils/phoneFormat";
import { formatTimestampForTable } from "../shared/utils/dateDisplay";
import { displayEmailOrDash } from "../shared/utils/displayEmail";
import {
  getVolunteerParticipantDisplayName,
  getVolunteerParticipantNameForSort,
} from "../shared/utils/volunteerOpportunityDisplay";

export const VO_DETAIL_TAB_DETAILS = "details";
export const VO_DETAIL_TAB_VOLUNTEERS = "volunteers";

const SORTABLE_KEYS = ["participant_name", "signup_date", "email", "phone"];

const tabBtnStyle = (active) => ({
  padding: "4px 10px",
  fontSize: "12px",
  fontWeight: 600,
  border: "1px solid #dee2e6",
  borderRadius: "4px",
  background: active ? "#0d6efd" : "#fff",
  color: active ? "#fff" : "#495057",
  cursor: "pointer",
});

const tableWrapStyle = {
  overflowX: "auto",
  maxHeight: "280px",
  overflowY: "auto",
  border: "1px solid #dee2e6",
  borderRadius: "4px",
};
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: "12px" };
const thStyle = { padding: "4px 6px", textAlign: "left", fontWeight: 600 };
const tdStyle = { padding: "4px 6px" };
const theadTrStyle = { borderBottom: "1px solid #dee2e6", background: "#f8f9fa" };
const tbodyTrStyle = { borderBottom: "1px solid #eee" };

function sortSignups(list, key, direction) {
  if (!key || !list.length) return list;
  const dir = direction === "asc" ? 1 : -1;
  return [...list].sort((a, b) => {
    if (key === "signup_date") {
      const t = (p) => {
        const raw = p?.signup_date;
        if (raw == null || raw === "") return 0;
        const ms = new Date(raw).getTime();
        return Number.isNaN(ms) ? 0 : ms;
      };
      return (t(a) - t(b)) * dir;
    }
    if (key === "participant_name") {
      return getVolunteerParticipantNameForSort(a).localeCompare(
        getVolunteerParticipantNameForSort(b),
        undefined,
        { numeric: true }
      ) * dir;
    }
    const av = (a[key] ?? "").toString().toLowerCase();
    const bv = (b[key] ?? "").toString().toLowerCase();
    return av.localeCompare(bv, undefined, { numeric: true }) * dir;
  });
}

export function VolunteerOpportunityDetailTabBar({
  tab,
  onTabChange,
  volunteerCount = 0,
  showVolunteersTab = true,
}) {
  if (!showVolunteersTab) return null;
  return (
    <div
      style={{
        display: "flex",
        gap: "4px",
        flexWrap: "wrap",
        padding: "0 16px 8px",
        borderBottom: "1px solid #dee2e6",
      }}
    >
      <button
        type="button"
        onClick={() => onTabChange(VO_DETAIL_TAB_DETAILS)}
        style={tabBtnStyle(tab === VO_DETAIL_TAB_DETAILS)}
      >
        Details
      </button>
      <button
        type="button"
        onClick={() => onTabChange(VO_DETAIL_TAB_VOLUNTEERS)}
        style={tabBtnStyle(tab === VO_DETAIL_TAB_VOLUNTEERS)}
      >
        Volunteers signed up ({volunteerCount})
      </button>
    </div>
  );
}

export function VolunteerOpportunitySignupsTab({ signups = [], loading = false }) {
  const [sortConfig, setSortConfig] = useState({ key: "participant_name", direction: "asc" });

  const handleSort = (key) => {
    if (!SORTABLE_KEYS.includes(key)) return;
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortIndicator = (key) =>
    sortConfig.key === key ? (sortConfig.direction === "asc" ? " ↑" : " ↓") : "";

  const sortedSignups = useMemo(
    () => sortSignups(signups, sortConfig.key, sortConfig.direction),
    [signups, sortConfig.key, sortConfig.direction]
  );

  const sortableThStyle = (key) =>
    SORTABLE_KEYS.includes(key)
      ? { ...thStyle, cursor: "pointer", userSelect: "none" }
      : thStyle;

  if (loading) {
    return <p style={{ color: "#6c757d", margin: 0, padding: "12px 16px" }}>Loading…</p>;
  }

  if (!sortedSignups.length) {
    return (
      <p style={{ color: "#6c757d", margin: 0, padding: "12px 16px" }}>
        No volunteers have signed up yet.
      </p>
    );
  }

  return (
    <div style={{ padding: "12px 16px" }}>
      <div style={tableWrapStyle}>
        <table style={tableStyle}>
          <thead>
            <tr style={theadTrStyle}>
              <th style={sortableThStyle("participant_name")} onClick={() => handleSort("participant_name")}>
                Name{sortIndicator("participant_name")}
              </th>
              <th style={sortableThStyle("email")} onClick={() => handleSort("email")}>
                Email{sortIndicator("email")}
              </th>
              <th style={sortableThStyle("phone")} onClick={() => handleSort("phone")}>
                Phone{sortIndicator("phone")}
              </th>
              <th style={sortableThStyle("signup_date")} onClick={() => handleSort("signup_date")}>
                Signed up{sortIndicator("signup_date")}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedSignups.map((row) => (
              <tr key={row._id || row.user_id} style={tbodyTrStyle}>
                <td style={tdStyle}>{getVolunteerParticipantDisplayName(row)}</td>
                <td style={tdStyle}>{displayEmailOrDash(row.email)}</td>
                <td style={tdStyle}>{row.phone ? formatPhoneDisplay(row.phone) : "—"}</td>
                <td style={tdStyle}>{formatTimestampForTable(row.signup_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
